import { useCallback, useEffect, useRef, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { changeIcon, getIcon } from 'react-native-change-icon';
import { CHRISTMAS_APP_ICON, DEFAULT_APP_ICON, getAppIconByAssetName, iconStrings, } from 'features/app-icons/appIcons';
import { Alert, AppState, BackHandler } from 'react-native';
import Sentry from 'utils/sentry';
import { isAndroid } from '../../../constants';
const STORE_KEY = 'ICON_NAME_2021';
const STORE_USED_CHRISTMAS_KEY = 'ICON_USED_CHRISTMAS_2025';
export const isChristmasIconAvailable = () => {
    const now = new Date();
    return (now.getFullYear() === 2025 &&
        now.getMonth() === 11 &&
        now.getDate() >= 1 &&
        now.getDate() <= 31);
};
/**
 * WARNING: library 'react-native-change-icon' provides 'resetIcon' method but it does not work.
 * It is an outdated library. Until we use a replacement, refer to its source code to achieve the wanted effect.
 */
const resetIcon = () => isAndroid ? changeIcon(DEFAULT_APP_ICON.assetName) : changeIcon();
/**
 * This works on both iOS and Android. But we are using it only on iOS, when APP starts.
 *
 * Changing the APP icon closes the APP on Android, so we are running this routine when the APP closes.
 * See AppShutdownWorker.kt for the Android implementation.
 */
export const changeChristmasIcon = () => {
    const available = isChristmasIconAvailable();
    if (available) {
        Promise.all([AsyncStorage.getItem(STORE_USED_CHRISTMAS_KEY), getIcon()])
            .then(([used, currentIconAssetName]) => {
            if (used) {
                // do nothing whether the current icon is christmas or not
                return;
            }
            AsyncStorage.setItem(STORE_USED_CHRISTMAS_KEY, '1');
            if (currentIconAssetName === CHRISTMAS_APP_ICON.assetName) {
                return;
            }
            changeIcon(CHRISTMAS_APP_ICON.assetName);
            // STORE_KEY keeps storing user's preferred icon
        })
            .catch((err) => {
            Sentry.logError({
                message: `Failed to changeIcon to ${CHRISTMAS_APP_ICON.assetName}`,
                err,
            });
        });
    }
    else {
        // restore
        AsyncStorage.removeItem(STORE_USED_CHRISTMAS_KEY);
        Promise.all([getStoredAppIcon(), getIcon()])
            .then(([storedAppIcon, currentIconAssetName]) => {
            if (currentIconAssetName !== CHRISTMAS_APP_ICON.assetName) {
                return;
            }
            const revertIcon = () => {
                // revert icon
                if (!storedAppIcon ||
                    storedAppIcon.name === CHRISTMAS_APP_ICON.name ||
                    storedAppIcon.name === DEFAULT_APP_ICON.name) {
                    resetIcon()
                        .then(() => {
                        AsyncStorage.removeItem(STORE_KEY);
                    })
                        .catch((err) => {
                        Sentry.logError({ message: 'Failed to resetIcon', err });
                    });
                }
                else {
                    changeIcon(storedAppIcon.assetName).catch((err) => {
                        AsyncStorage.removeItem(STORE_KEY);
                        Sentry.logError({
                            message: `Failed to changeIcon to ${storedAppIcon.assetName}`,
                            err,
                        });
                        resetIcon().catch((err) => {
                            Sentry.logError({ message: 'Also failed to resetIcon', err });
                        });
                    });
                }
            };
            if (AppState.currentState === 'active') {
                revertIcon();
            }
            else {
                // iOS rejects setAlternateIconName: when the app is not in the foreground.
                // Defer the restore until the next time the app becomes active.
                const subscription = AppState.addEventListener('change', (nextState) => {
                    if (nextState === 'active') {
                        subscription.remove();
                        // Only revert if Christmas is still over (guards against re-entry in Dec).
                        if (!isChristmasIconAvailable()) {
                            revertIcon();
                        }
                    }
                });
            }
        })
            .catch((err) => {
            Sentry.logError({ message: 'Failed to getIcon', err });
        });
    }
};
const storeAppIcon = (appIcon) => AsyncStorage.setItem(STORE_KEY, JSON.stringify(appIcon));
const getStoredAppIcon = async () => {
    const appIconStr = await AsyncStorage.getItem(STORE_KEY);
    try {
        return appIconStr ? JSON.parse(appIconStr) : undefined;
    }
    catch {
        return appIconStr ? getAppIconByAssetName(appIconStr) : undefined;
    }
};
export const useAppIcon = () => {
    const [value, setValue] = useState();
    const valueName = useRef(undefined);
    const setAppIconImpl = useCallback(async (newIcon) => {
        try {
            if (newIcon.name === DEFAULT_APP_ICON.name) {
                await resetIcon();
            }
            else {
                await changeIcon(newIcon.assetName);
            }
            setValue(newIcon);
            valueName.current = newIcon.name;
            await storeAppIcon(newIcon);
        }
        catch (err) {
            Sentry.logError({
                message: `Failed to set app icon ${newIcon.assetName}`,
                err,
            });
        }
    }, []);
    const setAppIcon = useCallback((newIcon) => {
        if (valueName.current !== newIcon?.name) {
            if (isAndroid) {
                Alert.alert(`${iconStrings[newIcon.name]} Icon`, 'To change the icon, we will need to turn off the Emma app', [
                    {
                        text: 'Cancel',
                        style: 'cancel',
                    },
                    {
                        text: 'Okay',
                        onPress: () => {
                            setAppIconImpl(newIcon);
                            BackHandler.exitApp();
                        },
                    },
                ], { cancelable: true });
            }
            else {
                setAppIconImpl(newIcon);
            }
        }
    }, [setAppIconImpl]);
    useEffect(() => {
        getIcon()
            .then((currentIconAssetName) => {
            const icon = getAppIconByAssetName(currentIconAssetName) || DEFAULT_APP_ICON;
            setValue(icon);
        })
            .catch((err) => {
            Sentry.logError({ message: 'Failed to getIcon (effect hook)', err });
            setValue(DEFAULT_APP_ICON);
        });
    }, []);
    return [value, setAppIcon];
};
export default useAppIcon;
